import React, { createContext, useContext, useState } from 'react';
import { getNotifications, resendNotification } from '../stubs/notifications';
import { Notification } from '../types/notification';

interface NotificationContextType {
  notifications: Notification[];
  loading: boolean;
  error: string | null;
  fetchNotifications: () => Promise<void>;
  updateNotificationStatus: (id: number, status: string) => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const data = await getNotifications();
      setNotifications(data);
      setError(null);
    } catch (err) {
      setError('Failed to fetch notifications');
    } finally {
      setLoading(false);
    }
  };

  const updateNotificationStatus = async (id: number, status: string) => {
    try {
      setLoading(true);
      await resendNotification(id);
      await fetchNotifications();
      setError(null);
    } catch (err) {
      setError('Failed to update notification status');
    } finally {
      setLoading(false);
    }
  };

  return (
    <NotificationContext.Provider value={{ notifications, loading, error, fetchNotifications, updateNotificationStatus }}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};
